import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Container, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  cancelOrgInvitation,
  fetchOrgMembers,
  inviteOrgMember,
  listOrgInvitations,
  removeOrgMember,
} from "../../lib/api"
import { notifyError, notifySuccess } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { MembersView } from "./-members/MembersView"
import type { IMemberRow } from "./-members/MembersView/interfaces"

export const Route = createFileRoute("/_auth/members")({
  component: MembersPage,
})

function MembersPage(): React.JSX.Element {
  const company = useCompanyApi()
  const { user } = useAuth()
  const { i18n } = useLingui()
  const queryClient = useQueryClient()

  const { data: members, isLoading } = useCompanyQuery({
    key: queryKeys.orgMembers,
    queryFn: (company) => fetchOrgMembers(company),
  })

  const { data: invites } = useCompanyQuery({
    key: queryKeys.orgInvites,
    queryFn: (company) => listOrgInvitations(company),
  })

  const myRole = members?.find((m) => m.userId === user?.id)?.role
  const canManage = !!user?.isAdmin || myRole === "owner" || myRole === "admin"

  const { mutate: invite, isPending: inviting } = useCompanyMutation({
    mutationFn: (company, vars: { email: string; role: "member" | "admin" }) => inviteOrgMember(company, vars),
    onSuccess: () => {
      notifySuccess(i18n._(msg`Your teammate has been invited`), i18n._(msg`Invitation sent`))
      queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites() })
    },
    onError: (err) => notifyError(err),
  })

  const { mutate: revoke } = useCompanyMutation({
    mutationFn: (company, invitationId: string) => cancelOrgInvitation(company, invitationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgInvites() }),
    onError: (err) => notifyError(err),
  })

  const { mutate: remove } = useCompanyMutation({
    mutationFn: (company, memberId: string) => removeOrgMember(company, memberId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgMembers() }),
    onError: (err) => notifyError(err),
  })

  const promptRemove = (member: IMemberRow): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Remove ${member.email}?`),
      children: <Text size="sm">{i18n._(msg`They will lose access to this workspace.`)}</Text>,
      labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => remove(member.id),
    })
  }

  if (!company) {
    return (
      <Container size="md" py="xl">
        <Text c="dimmed">{i18n._(msg`Select a workspace to manage its members.`)}</Text>
      </Container>
    )
  }

  return (
    <MembersView
      members={members ?? []}
      invites={invites ?? []}
      currentUserId={user?.id ?? ""}
      canManage={canManage}
      isLoading={isLoading}
      inviting={inviting}
      onInvite={(email, role) => invite({ email, role })}
      onRevoke={revoke}
      onRemove={promptRemove}
    />
  )
}
